import { getCollectionValues, getNumber, getString, isRecord } from './sanitize';
import type { NormalizedError } from './types';

const getErrorFields = (value: Record<string, unknown>) => {
  const extensions = isRecord(value.extensions) ? value.extensions : undefined;
  const response = isRecord(value.response) ? value.response : undefined;

  return {
    classification: getString(extensions?.classification ?? value.classification),
    code: getString(extensions?.code ?? value.code),
    message:
      getString(value.message) ?? getString(value.error) ?? getString(value.reason) ?? getString(response?.statusText),
    name: getString(value.name),
    path: Array.isArray(value.path) ? value.path.map((item) => String(item)).join('.') : getString(value.path),
    stack: getString(value.stack),
    statusCode: getNumber(value.statusCode ?? value.status ?? extensions?.statusCode ?? response?.status),
  };
};

const normalizeGraphQlError = (value: unknown): NormalizedError | null => {
  if (!isRecord(value)) return null;
  const fields = getErrorFields(value);

  return {
    source: 'graphql',
    message: fields.message ?? 'GraphQL error',
    classification: fields.classification,
    code: fields.code,
    path: fields.path,
    statusCode: fields.statusCode,
    name: fields.name,
    stack: fields.stack,
  };
};

const normalizeGenericError = (
  value: unknown,
  source: NormalizedError['source'] = 'unknown'
): NormalizedError | null => {
  if (typeof value === 'string') {
    return {
      source: 'message',
      message: value,
      classification: 'CUSTOM_ERROR',
    };
  }

  if (value instanceof Error) {
    const enrichedError = value as Error & { status?: unknown; statusCode?: unknown; code?: unknown };

    return {
      source: source === 'unknown' ? 'exception' : source,
      message: value.message || value.name || 'Unexpected error',
      name: value.name,
      stack: value.stack,
      code: getString(enrichedError.code),
      statusCode: getNumber(enrichedError.statusCode ?? enrichedError.status),
    };
  }

  if (!isRecord(value)) return null;
  const fields = getErrorFields(value);

  if (!fields.message) return null;

  return {
    source,
    message: fields.message,
    classification: fields.classification,
    code: fields.code,
    path: fields.path,
    statusCode: fields.statusCode,
    name: fields.name,
    stack: fields.stack,
  };
};

export const normalizeErrors = (error: unknown): NormalizedError[] => {
  const normalizedErrors: NormalizedError[] = [];
  const pushError = (entry: NormalizedError | null) => {
    if (entry) normalizedErrors.push(entry);
  };
  const pushCollectionErrors = (value: unknown, source: 'graphql' | 'network') => {
    getCollectionValues(value).forEach((entry) => {
      pushError(normalizeGraphQlError(entry) ?? normalizeGenericError(entry, source));
    });
  };

  if (typeof error === 'string') {
    pushError(normalizeGenericError(error));
  } else if (error instanceof Error) {
    pushError(normalizeGenericError(error, 'exception'));
  }

  if (isRecord(error)) {
    pushCollectionErrors(error.errors, 'graphql');
    pushCollectionErrors(error.graphQLErrors, 'graphql');

    const networkError = error.networkError;
    if (networkError instanceof Error) {
      pushError(normalizeGenericError(networkError, 'network'));
    } else if (isRecord(networkError)) {
      const networkResult = isRecord(networkError.result) ? networkError.result : undefined;

      pushCollectionErrors(networkResult?.errors, 'network');
      pushError(normalizeGenericError(networkError, 'network'));
    }

    const causeError = error.cause;
    if (normalizedErrors.length === 0) {
      pushError(normalizeGenericError(causeError, 'exception'));
      pushError(normalizeGenericError(error, 'unknown'));
    }
  }

  if (!normalizedErrors.length) {
    pushError({
      source: 'unknown',
      message: 'unknown-error',
    });
  }

  return Array.from(
    normalizedErrors
      .reduce((uniqueErrors, entry) => {
        const key = [
          entry.source,
          entry.classification ?? '',
          entry.code ?? '',
          entry.path ?? '',
          entry.statusCode ?? '',
          entry.message,
        ].join('|');

        if (!uniqueErrors.has(key)) uniqueErrors.set(key, entry);
        return uniqueErrors;
      }, new Map<string, NormalizedError>())
      .values()
  );
};

export const getExceptionToCapture = (error: unknown): Error | null => {
  if (error instanceof Error) return error;
  if (!isRecord(error)) return null;

  if (error.networkError instanceof Error) return error.networkError;
  if (error.cause instanceof Error) return error.cause;
  if (error.originalError instanceof Error) return error.originalError;

  const message = getString(error.message);
  const stack = getString(error.stack);
  const name = getString(error.name);

  if (!message || !stack) return null;

  const syntheticError = new Error(message);
  syntheticError.name = name ?? 'CapturedError';
  syntheticError.stack = stack;
  return syntheticError;
};
